import type {
  ApiClientMembershipsEnvelope,
  FetchClientMembershipsArgs,
} from '../../api/types/memberships.types';

import { queryKeys } from '../../queryKeys';
import { useQuery } from '../base';

/**
 * T10 (spec §3.3): this returns the ENVELOPE — rows plus the server-computed
 * `summary`/`coverage` of exactly those rows — never the bare array it used
 * to. A caller that only needs the rows uses `useClientMembershipRows` below,
 * which selects them out of the same cache entry.
 *
 * The optional cohort filters are part of the query key, so switching the
 * range or the type selection is a different entry: the panel shows its
 * loading state instead of the previous cohort's totals.
 */
export default function useClientMemberships({
  clientId,
  from,
  membershipIds,
  organizationId,
  to,
}: FetchClientMembershipsArgs) {
  const { queryFn, queryKey } = queryKeys.memberships.fetchClientMemberships({
    clientId,
    from,
    membershipIds,
    organizationId,
    to,
  });
  return useQuery({
    enabled: !!clientId && !!organizationId,
    queryFn,
    queryKey,
  });
}

/** The rows alone, for a caller with no interest in the money summary. */
export function useClientMembershipRows(
  args: FetchClientMembershipsArgs,
  options?: { refetchOnWindowFocus?: boolean },
) {
  const { queryFn, queryKey } =
    queryKeys.memberships.fetchClientMemberships(args);
  return useQuery({
    enabled: !!args.clientId && !!args.organizationId,
    queryFn,
    queryKey,
    select: (envelope: ApiClientMembershipsEnvelope) => envelope.data,
    ...options,
  });
}
